import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsAlt,
  faBolt,
  faLevelDownAlt,
} from '@fortawesome/free-solid-svg-icons';
import CustomCheckbox from '../../../../components/Inputs/CustomCheckbox';
import styled from '@emotion/styled';
import CustomButton from '../../../../components/Buttons/CustomButton';
import GoBack from '../../Helpers/GoBack';

const TextArea = styled.textarea`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 10px;
  background: #1d265b;
  color: #ffffffa3;
  margin-bottom: 10px;
  border-color: white;
`;

const checkboxStyle = {
  paddingBottom: '5px',
  paddingTop: '5px',
  display: 'flex',
  marginLeft: 10,
};

const NoteButtons = styled.div`
  display: flex;
  font-size: 12px;
  flex-direction: column;
  button {
    white-space: nowrap;
    height: 35px;
    margin-bottom: 20px;
  }
`;

export default function NoteOptions(props) {
  const {
    setSelectedNote,
    selectedNote,
    notesObj,
    setNotesObj,
    updateLayout,
    kit,
    setAllNotes,
    allNotes,
    componentAssignments,
    InputGroup,
    SubmitSection,
    notShownQuantityOfSelectedNote,
    resetLabel,
    kitDetails,
  } = props;

  const resetNotePosition = () => {
    const labelData = getTemplateData();
    const cavity = kitDetails.tray.cavities.find(
      (cavity) => cavity.id === selectedNote.cavityId,
    );
    const [, bottomLeftPosition] = JSON.parse(cavity?.vertices || '[]');

    const position = labelData
      ? { x: labelData.x, y: labelData.y }
      : bottomLeftPosition;

    const newNote = {
      ...selectedNote,
      position,
      rotation: labelData?.rotation,
    };

    const notes = notesObj.map((note) =>
      newNote.id === note.id ? newNote : note,
    );
    setNotesObj(notes);
    setSelectedNote(newNote);
    updateLayout(notes, kit?.id);
  };

  const rotateNote = () => {
    const notes = notesObj.map((note) =>
      note.id === selectedNote.id
        ? { ...note, rotation: note.rotation ? 0 : 90 }
        : note,
    );

    updateLayout(notes, kit?.id);
    setNotesObj(notes);
  };

  const deleteNote = (id) => {
    setAllNotes(allNotes.filter((x) => x.id !== id));
    const notes = notesObj.filter((x) => x.id !== id);
    setNotesObj(notes);
    setSelectedNote(null);
    updateLayout(notes, kit?.id);
  };

  const getMappedNotes = ({ id, name, value, isOverridden }) => {
    return notesObj.map((note) =>
      id === note.id ? { ...note, [name]: value, isOverridden } : note,
    );
  };

  const updateNoteValue = (id, name, value, isOverridden) => {
    const notes = getMappedNotes({ id, name, value, isOverridden });
    setNotesObj(notes);
    setAllNotes(notes);
    setSelectedNote({ ...selectedNote, [name]: value, isOverridden });
    updateLayout(notes, kit?.id);
  };

  function condition(item) {
    return (
      item.cavityId === selectedNote.cavityId &&
      item.component.id === selectedNote.componentId
    );
  }

  function getTemplateData() {
    const { imageOverrideJson, imageUrl } =
      componentAssignments.find(condition) ?? {};

    const mapJsonToObject = JSON.parse(imageOverrideJson ?? null) ?? [];
    const { labelData } =
      mapJsonToObject.find((img) => img.imageUrl === imageUrl) ?? {};

    return labelData;
  }

  const resetLabelText = () => {
    const id = selectedNote.id;
    const name = 'isOverridden';
    const value = false;
    const isOverridden = false;
    const notes = getMappedNotes({ id, name, value, isOverridden });
    setAllNotes(notes);

    setNotesObj(notes);
    setSelectedNote({ ...selectedNote, [name]: value, isOverridden });

    resetLabel(notes, kit?.id);
  };

  return (
    <>
      <GoBack title="Edit note" goBack={() => setSelectedNote(null)} />
      <InputGroup width={100}>
        <TextArea
          value={decodeURI(selectedNote.text)}
          name="Text1"
          cols="40"
          rows="5"
          onChange={(e) =>
            updateNoteValue(
              selectedNote.id,
              'text',
              encodeURI(e.target.value),
              true,
            )
          }
          onFocus={(e) => {
            setSelectedNote((prev) => ({
              ...prev,
              text: e.target.value === 'Edit me' ? '' : prev.text,
            }));
          }}
        />
        <span style={{ color: 'orange', textAlign: 'center' }}>
          Warning “Note: Any changes to component label will override any
          auto-generated text (ex. Quantity)”
        </span>
        <br />
        <CustomCheckbox
          label={'Mark as highlighted'}
          onChange={() =>
            updateNoteValue(
              selectedNote.id,
              'highlighted',
              !selectedNote.highlighted,
              selectedNote.isOverridden,
            )
          }
          checked={selectedNote.highlighted}
          style={checkboxStyle}
        />
        <CustomCheckbox
          label={'Hide this note'}
          onChange={() =>
            updateNoteValue(
              selectedNote.id,
              'isHidden',
              !selectedNote.isHidden,
              selectedNote.isOverridden,
            )
          }
          checked={selectedNote.isHidden}
          style={checkboxStyle}
        />
        <br />
        <NoteButtons>
          <CustomButton
            rounded
            label="Rotate to 90 deg"
            icon={<FontAwesomeIcon icon={faLevelDownAlt} />}
            onClick={rotateNote}
            background={'cadetblue'}
          />
          {selectedNote?.isOverridden && (
            <CustomButton
              label={'Reset Label Text'}
              onClick={resetLabelText}
              rounded
              icon={<FontAwesomeIcon icon={faBolt} />}
              background={'orange'}
            />
          )}
          <CustomButton
            rounded
            label="Reset Label Position"
            icon={<FontAwesomeIcon icon={faArrowsAlt} />}
            onClick={resetNotePosition}
            background={'#61a431'}
          />
        </NoteButtons>
        {!!notShownQuantityOfSelectedNote && (
          <span style={{ color: 'orange', textAlign: 'center' }}>
            This component has Qty {notShownQuantityOfSelectedNote} currently
            unshown
          </span>
        )}
      </InputGroup>
      <SubmitSection selectedNote={selectedNote}>
        <CustomButton
          rounded
          label={'Delete note'}
          background={`orangered`}
          onClick={() => deleteNote(selectedNote.id)}
        />
      </SubmitSection>
    </>
  );
}
